import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons/faFileAlt';
import moment from 'moment';

import { roundTo } from '../../../../../../../helpers/helpers';
import capitalize from '../../../../../../../helpers/capitalize';
import { checkAndRoun } from '../../../../../../../helpers/topicsHelpers';
import convertPermalinkToPath from '../../../../../../../helpers/urlHelpers';
import { UKDisableMessage } from '../../../../../../../data/ukPrivateCompanyInsights';

import Tooltip from '../../../../../../Tooltip';
import ConditionalTooltip from '../../../../../../UKPrivateCompanyInsights/CommonComponent/ConditionalTooltip';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const TabTableSection = (props) => {
  const {
    currentPage, pageSize, currentData, totalValue, hasInsightsAccess, hasInsightsTrialAccess,
    sortField, sortOrder, onSort,
  } = props;
  const tableBodyRef = useRef();

  const disableMessage = UKDisableMessage(true);
  return (
    <div className={cx('investors', { no_access: !hasInsightsAccess })}>
      <div className={Styles['investors-header']}>
        <div className={Styles['investors-row']}>
          <div className={cx('investors-cell', 'rowNumber')}>#</div>
          <div className={cx('investors-cell', 'investorName')}>Investor’s Name</div>
          <div className={cx('investors-cell', 'classOfShare')}>Class of Shares</div>
          <div className={cx('investors-cell', 'numberOfShares')}>
            <div className={Styles['table-sorting']} onClick={() => onSort('numberOfShares')}>
              <div>
                Number of Shares
              </div>
              <ConditionalTooltip
                tooltipProps={{
                  hasBigScreenDesign: true,
                  isCentered: true,
                  dynamicPosition: true,
                  contentStyle: { left: 0 },
                  className: Styles['investors-cell-tooltip'],
                }}
                tooltipMessage={disableMessage}
                showTooltip={hasInsightsTrialAccess}
              >
                <div className={cx('sort-arrows')}>
                  {(sortField !== 'numberOfShares' || sortOrder === 'asc') && (
                    <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                  )}
                  {(sortField !== 'numberOfShares' || sortOrder === 'desc') && (
                    <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                  )}
                </div>
              </ConditionalTooltip>
            </div>
            <Tooltip
              newIcon
              hasBigScreenDesign
              isCentered
              className={Styles.sharePrice_tooltip}
            >
              The total number of shares held by the investor in the share class.
            </Tooltip>
          </div>
          <div className={cx('investors-cell', 'shareholdingPercentage')}>
            <div className={Styles['table-sorting']} onClick={() => onSort('shareholdingPercentage')}>
              <div>
                % of Shareholding
              </div>
              <ConditionalTooltip
                tooltipProps={{
                  hasBigScreenDesign: true,
                  isCentered: true,
                  dynamicPosition: true,
                  contentStyle: { left: 0 },
                  className: Styles['investors-cell-tooltip'],
                }}
                tooltipMessage={disableMessage}
                showTooltip={hasInsightsTrialAccess}
              >
                <div className={cx('sort-arrows')}>
                  {(sortField !== 'shareholdingPercentage' || sortOrder === 'asc') && (
                    <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                  )}
                  {(sortField !== 'shareholdingPercentage' || sortOrder === 'desc') && (
                    <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                  )}
                </div>
              </ConditionalTooltip>
            </div>
            <Tooltip
              newIcon
              hasBigScreenDesign
              isCentered
              className={Styles.sharePrice_tooltip}
            >
              The percentage of shares held by the investor out of all shares.
            </Tooltip>
          </div>
          <div className={cx('investors-cell', 'valueOfHoldings')}>
            <span>
              Value of Holdings
            </span>
            <Tooltip
              newIcon
              hasBigScreenDesign
              isCentered
              dynamicPosition
              contentStyle={{ left: 0 }}
              className={Styles.sharePrice_tooltip}
            >
              The total value of holdings for the investor based on the
              last known price per share, as per the reported date.
            </Tooltip>
          </div>
          <div className={cx('investors-cell', 'expandIcon')} />
        </div>
        <div
          className={cx('investors-row', 'total_row')}
        >
          <div className={cx('investors-cell', 'rowNumber')} />
          <div className={cx('investors-cell', 'investorName')}>
            TOTAL
          </div>
          <div className={cx('investors-cell', 'classOfShare')} />
          <div className={cx('investors-cell', 'numberOfShares')} />
          <div className={cx('investors-cell', 'shareholdingPercentage')} />
          <div className={cx('investors-cell', 'valueOfHoldings')}>
            {checkAndRoun(totalValue.valueOfHoldings, 2, true)}
          </div>
          <div className={cx('investors-cell', 'expandIcon')} />
        </div>
      </div>
      {!hasInsightsAccess && (
        <div className={Styles['investors-access']}>
          <Link to="/pricing">Upgrade to UK Insights</Link>
          {' '}
          to use this feature.
        </div>
      )}
      <div className={Styles['investors-body']} ref={tableBodyRef}>
        {currentData.data
          .slice(((currentPage - 1) * pageSize), (((currentPage - 1) * pageSize) + pageSize))
          .map((obj, i) => (
            <TableRow
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              currentData={obj}
              currentRow={((currentPage - 1) * pageSize) + (i + 1)}
            />
          ))}
      </div>
    </div>
  );
};

const TableRow = (props) => {
  const { currentData, currentRow } = props;
  const [isExpand, setIsExpand] = useState(false);

  return (
    <div className={cx('investors-row-holder')}>
      <div className={Styles['investors-row']}>
        <div className={cx('investors-cell', 'rowNumber')}>
          {currentRow}
        </div>
        <div className={cx('investors-cell', 'investorName')}>
          {currentData.investorPermalink ? (
            <Link to={convertPermalinkToPath(currentData.investorPermalink)}>
              {currentData.investorName || '-'}
            </Link>
          ) : (
            <>
              {currentData.investorName || '-'}
            </>
          )}
        </div>
        <div className={cx('investors-cell', 'classOfShare')}>
          {capitalize(currentData.classOfShares || '-')}
        </div>
        <div className={cx('investors-cell', 'numberOfShares')}>
          {(currentData.numberOfShares || '').toLocaleString() || '-'}
        </div>
        <div className={cx('investors-cell', 'shareholdingPercentage')}>
          {typeof currentData.shareholdingPercentage === 'number' ? `${roundTo(currentData.shareholdingPercentage, 3)}%` : '-'}
        </div>
        <div className={cx('investors-cell', 'valueOfHoldings')}>
          {currentData.valueOfHoldings ? checkAndRoun(currentData.valueOfHoldings, 2, true, true, true) : '-'}
        </div>
        <div
          className={cx('investors-cell', 'expandIcon')}
          onClick={() => setIsExpand(!isExpand)}
        >
          <FontAwesomeIcon icon={isExpand ? faChevronUp : faChevronDown} />
        </div>
      </div>
      {isExpand && (
      <div className={cx('investors-extra')}>
        <div className={cx('investors-extra-cell', 'classOfShare')}>
          <div className={cx('investors-extra-title')}>
            Class of Shares
          </div>
          <div className={cx('investors-extra-value')}>
            {capitalize(currentData.classOfShares || '-')}
          </div>
        </div>
        <div className={cx('investors-extra-cell', 'shareholdingPercentage')}>
          <div className={cx('investors-extra-title')}>
            % of Shareholding
            <Tooltip
              newIcon
              hasBigScreenDesign
              isCentered
              className={Styles.sharePrice_tooltip}
            >
              The percentage of shares held by the investor out of all shares.
            </Tooltip>
          </div>
          <div className={cx('investors-extra-value')}>
            {typeof currentData.shareholdingPercentage === 'number' ? `${roundTo(currentData.shareholdingPercentage, 3)}%` : '-'}
          </div>
        </div>
        <div className={cx('investors-extra-cell', 'currency')}>
          <div className={cx('investors-extra-title')}>
            Currency
          </div>
          <div className={cx('investors-extra-value')}>
            {currentData.currency || '-'}
          </div>
        </div>
        <div className={cx('investors-extra-cell', 'pricePerShare')}>
          <div className={cx('investors-extra-title', 'has_tooltip')}>
            <span>
              Adjusted Last
              <br />
              Round Share Price
            </span>
            <Tooltip
              newIcon
              hasBigScreenDesign
              isCentered
              dynamicPosition
              contentStyle={{ left: 0 }}
              className={Styles.sharePrice_tooltip}
            >
              The price per share for the share class adjusted for splits.
            </Tooltip>
          </div>
          <div className={cx('investors-extra-value')}>
            {currentData.pricePerShare ? checkAndRoun(currentData.pricePerShare, 2, false, false, true) : '-'}
          </div>
        </div>
        <div className={cx('investors-extra-cell', 'CS01filingDate')}>
          <div className={cx('investors-extra-title')}>
            <span>
              Date Reported
              <br />
              (number of shares)
            </span>
          </div>
          <div className={cx('investors-extra-value')}>
            {currentData.CS01filingDate ? moment(currentData.CS01filingDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''}
            <Tooltip
              hasBigScreenDesign
              className={Styles.infoIcon_tooltip}
              icon={(
                <Link
                  className={Styles.infoIcon}
                  to={convertPermalinkToPath(currentData.CS01filingUrl)}
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                </Link>
              )}
            >
              View document
            </Tooltip>
          </div>
        </div>
        <div className={cx('investors-extra-cell', 'SH01filingDate')}>
          <div className={cx('investors-extra-title')}>
            <span>
              Date Reported
              <br />
              (price per share)
            </span>
            <Tooltip
              newIcon
              hasBigScreenDesign
              isCentered
              className={Styles.sharePrice_tooltip}
            >
              The date the company reported the holdings, which may differ from the last round.
            </Tooltip>
          </div>
          <div className={cx('investors-extra-value')}>
            {currentData.SH01filingDate ? moment(currentData.SH01filingDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''}
            <Tooltip
              hasBigScreenDesign
              className={Styles.infoIcon_tooltip}
              icon={(
                <Link
                  className={Styles.infoIcon}
                  to={convertPermalinkToPath(currentData.SH01filingDate)}
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                </Link>
              )}
            >
              View document
            </Tooltip>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default TabTableSection;
